import React, { useEffect, useState } from 'react';
import { adminApi, bannerUrl } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Upload, Loader2, ImageIcon, MessageSquare } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const PAGE_OPTIONS = [
  { value: '/', label: 'Home' },
  { value: '/loan', label: 'Personal Loans' },
  { value: '/protect-credit', label: 'Protect Credit' },
  { value: '/build-credit', label: 'Financials' },
  { value: '/how-it-works', label: 'How It Works' },
  { value: '/faq', label: 'FAQ' },
];

const emptyForm = {
  page: '/', title: '', description: '', image_url: '',
  cta_label: 'Learn More', cta_url: '', active: true,
};

export default function AdminPopups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listPopups();
      setItems(data || []);
    } catch (e) { toast({ title: 'Failed to load popups' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      page: p.page, title: p.title || '', description: p.description || '',
      image_url: p.image_url || '', cta_label: p.cta_label || 'Learn More',
      cta_url: p.cta_url || '', active: p.active !== false,
    });
    setOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const res = await adminApi.uploadFile(file);
      setForm((f) => ({ ...f, image_url: res.url }));
      toast({ title: 'Image uploaded' });
    } catch (err) { toast({ title: 'Upload failed' }); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.title || !form.page) return toast({ title: 'Title and page are required' });
    setSaving(true);
    try {
      if (editing) await adminApi.updatePopup(editing.id, form);
      else await adminApi.createPopup(form);
      setOpen(false);
      await load();
      toast({ title: editing ? 'Popup updated' : 'Popup created' });
    } catch (err) { toast({ title: 'Save failed' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete popup "${p.title}"?`)) return;
    try { await adminApi.deletePopup(p.id); await load(); toast({ title: 'Deleted' }); }
    catch { toast({ title: 'Delete failed' }); }
  };

  const toggleActive = async (p) => {
    try { await adminApi.updatePopup(p.id, { active: !p.active }); await load(); }
    catch { toast({ title: 'Update failed' }); }
  };

  const pageLabel = (page) => PAGE_OPTIONS.find((p) => p.value === page)?.label || page;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-slate-900">Popups</h1>
          <p className="text-slate-600 mt-1">Configure page-specific popups. Users see the most recent active popup per page (once, then dismissed).</p>
        </div>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="w-4 h-4 mr-1.5" /> New Popup
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-14 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-white border border-slate-200 p-10 text-center text-slate-500">
          <MessageSquare className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          No popups yet. Click “New Popup” to create one.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-4 flex flex-col md:flex-row gap-4 items-start md:items-center">
              <div className="w-24 h-24 rounded-md bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden shrink-0 flex items-center justify-center relative">
                {p.image_url ? (
                  <>
                    <img src={bannerUrl(p.image_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg opacity-50" />
                    <img src={bannerUrl(p.image_url)} alt="" className="relative w-full h-full object-contain object-center p-1.5" />
                  </>
                ) : <ImageIcon className="w-6 h-6 text-slate-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">{pageLabel(p.page)}</span>
                  <h3 className="font-semibold text-slate-900 truncate">{p.title}</h3>
                  <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${p.active ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-slate-100 text-slate-500'}`}>{p.active ? 'Active' : 'Inactive'}</span>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2 mt-1">{p.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} />
                <Button variant="outline" size="sm" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(p)} className="text-red-600 hover:bg-red-50 border-red-200"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Popup' : 'New Popup'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Show on Page</Label>
              <Select value={form.page} onValueChange={(v) => setForm({ ...form, page: v })}>
                <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAGE_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label} ({p.value})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 h-10" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 min-h-[80px]" />
            </div>
            <div>
              <Label className="text-xs">Image (banner top of popup, optional)</Label>
              <div className="mt-1 flex items-start gap-3">
                <div className="w-32 h-20 rounded-md bg-gradient-to-br from-emerald-50 to-teal-50 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0 relative">
                  {form.image_url ? (
                    <>
                      <img src={bannerUrl(form.image_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg opacity-50" />
                      <img src={bannerUrl(form.image_url)} alt="" className="relative w-full h-full object-contain object-center p-1.5" />
                    </>
                  ) : <ImageIcon className="w-6 h-6 text-slate-300" />}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm cursor-pointer">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload image
                    <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                  </label>
                  <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="h-10" placeholder="Or paste an image URL" />
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Button Label</Label>
                <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="mt-1 h-10" />
              </div>
              <div>
                <Label className="text-xs">Button Link</Label>
                <Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className="mt-1 h-10" placeholder="https://..." />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label className="text-sm">Active (visible on site)</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Save Changes' : 'Create Popup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
